import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search } from 'lucide-react';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', customer_number: '', short_name: '', street: '',
    postal_code: '', city: '', country: 'Deutschland',
    phone: '', email: '', website: '', notes: ''
  });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
      setFiltered(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!search) { setFiltered(customers); return; }
    setFiltered(customers.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.customer_number.toLowerCase().includes(search.toLowerCase()) ||
      c.city?.toLowerCase().includes(search.toLowerCase())
    ));
  }, [search, customers]);

  const handleSubmit = async () => {
    if (!form.name || !form.customer_number) {
      toast.error('Name und Kundennummer sind erforderlich');
      return;
    }
    try {
      if (editing) {
        await api.put('/customers/' + editing.id, form);
        toast.success('Kunde gespeichert');
      } else {
        await api.post('/customers', form);
        toast.success('Kunde angelegt');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', customer_number: '', short_name: '', street: '', postal_code: '', city: '', country: 'Deutschland', phone: '', email: '', website: '', notes: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (e, customer) => {
    e.stopPropagation();
    setEditing(customer);
    setForm({
      name: customer.name || '',
      customer_number: customer.customer_number || '',
      short_name: customer.short_name || '',
      street: customer.street || '',
      postal_code: customer.postal_code || '',
      city: customer.city || '',
      country: customer.country || 'Deutschland',
      phone: customer.phone || '',
      email: customer.email || '',
      website: customer.website || '',
      notes: customer.notes || '',
    });
    setShowForm(true);
  };

  const handleDeactivate = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Kunde wirklich deaktivieren?')) return;
    try {
      await api.delete('/customers/' + id);
      toast.success('Kunde deaktiviert');
      load();
    } catch (err) {
      toast.error('Fehler beim Deaktivieren');
    }
  };

  if (loading) return <div className="text-gray-500 text-sm">Laden...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Kunden</h2>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', customer_number: '', short_name: '', street: '', postal_code: '', city: '', country: 'Deutschland', phone: '', email: '', website: '', notes: '' }); }}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <Plus size={16} /> Neuer Kunde
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">{editing ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kundennummer *</label>
              <input type="text" value={form.customer_number} onChange={e => setForm({...form, customer_number: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kuerzel</label>
              <input type="text" value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Strasse</label>
              <input type="text" value={form.street} onChange={e => setForm({...form, street: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
              <input type="text" value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
              <input type="text" value={form.city} onChange={e => setForm({...form, city: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Webseite</label>
              <input type="text" value={form.website} onChange={e => setForm({...form, website: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSubmit}
              className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors">
              Speichern
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }}
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-6 py-2 rounded-lg transition-colors">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder="Suche nach Name, Kundennummer oder Stadt..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="divide-y divide-gray-100">
          {filtered.length === 0 && (
            <p className="text-sm text-gray-500 p-6">Keine Kunden gefunden</p>
          )}
          {filtered.map(c => (
            <div key={c.id}
              onClick={() => navigate('/customers/' + c.id)}
              className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{c.name}</p>
                  {!c.is_active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inaktiv</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {c.customer_number}
                  {c.city ? ' · ' + c.city : ''}
                  {c.phone ? ' · ' + c.phone : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={e => handleEdit(e, c)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium px-3 py-1 rounded-lg hover:bg-primary-50 transition-colors">
                  Bearbeiten
                </button>
                {c.is_active && (
                  <button onClick={e => handleDeactivate(e, c.id)}
                    className="text-xs text-red-500 hover:text-red-600 font-medium px-3 py-1 rounded-lg hover:bg-red-50 transition-colors">
                    Deaktivieren
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
